import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cjmsApi } from '../api/cjmsApi';

const BUSINESS_TYPES = [
    'Construction', 'Plumbing', 'Electrical', 'Cleaning', 'Landscaping',
    'HVAC', 'Automotive', 'Beauty', 'Fitness', 'Catering',
    'Events', 'Consulting', 'Real Estate', 'Photography', 'IT Services',
    'Education', 'Healthcare', 'Legal', 'Accounting',
];

interface AddLeadFormProps {
    onSuccess: () => void;
    onCancel: () => void;
}

export const AddLeadForm: React.FC<AddLeadFormProps> = ({ onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        businessType: '',
        source: 'manual',
        notes: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const validateEmail = (email: string) => {
        return /^[^\s@]+@[^\s@]+.[^\s@]+$/.test(email);
    };

    const validatePhone = (phone: string) => {
        return phone === '' || /^\+?[0-9\s\-\(\)]{7,}$/.test(phone);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
            setError('First name, last name, and email are required.');
            return;
        }

        if (!validateEmail(formData.email)) {
            setError('Please enter a valid email address.');
            return;
        }

        if (formData.phone && !validatePhone(formData.phone)) {
            setError('Please enter a valid phone number.');
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await cjmsApi.addLead({
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim() || undefined,
                company: formData.company.trim() || undefined,
                businessType: formData.businessType || undefined,
                source: formData.source,
                notes: formData.notes.trim() || undefined,
            });

            if (result.success) {
                onSuccess();
            } else {
                setError(result.error || 'Failed to add lead.');
            }
        } catch (err: any) {
            setError(err.message || 'Unexpected error adding lead.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-white rounded-xl shadow-sm border border-slate-200"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] font-semibold text-slate-600 uppercase mb-1">
                            First Name *
                        </label>
                        <input
                            type="text"
                            value={formData.firstName}
                            onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                            required
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-semibold text-slate-600 uppercase mb-1">
                            Last Name *
                        </label>
                        <input
                            type="text"
                            value={formData.lastName}
                            onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                            required
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-[10px] font-semibold text-slate-600 uppercase mb-1">
                        Email *
                    </label>
                    <input
                        type="email"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                </div>

                <div>
                    <label className="block text-[10px] font-semibold text-slate-600 uppercase mb-1">
                        Phone
                    </label>
                    <input
                        type="tel"
                        value={formData.phone}
                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+27..."
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                </div>

                <div>
                    <label className="block text-[10px] font-semibold text-slate-600 uppercase mb-1">
                        Company
                    </label>
                    <input
                        type="text"
                        value={formData.company}
                        onChange={e => setFormData({ ...formData, company: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    />
                </div>

                <div>
                    <label className="block text-[10px] font-semibold text-slate-600 uppercase mb-1">
                        Business Type
                    </label>
                    <select
                        value={formData.businessType}
                        onChange={e => setFormData({ ...formData, businessType: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 bg-white"
                    >
                        <option value="">Select industry...</option>
                        {BUSINESS_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-[10px] font-semibold text-slate-600 uppercase mb-1">
                        Notes
                    </label>
                    <textarea
                        value={formData.notes}
                        onChange={e => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Any initial context about this lead..."
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 resize-none"
                        rows={2}
                    />
                </div>

                {error && (
                    <div className="p-2 bg-red-50 text-red-600 text-xs rounded-lg border border-red-200">
                        {error}
                    </div>
                )}

                <div className="flex gap-2 pt-2">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                        {isSubmitting ? 'Adding...' : '+ Add Lead'}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-xs font-medium text-slate-600 bg-slate-100 rounded-md hover:bg-slate-200 transition-colors"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </motion.div>
    );
};